import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import type { SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  Input,
  ViewChild,
  ViewContainerRef,
  output,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import { deepEqual } from 'fast-equals';
import type { Observable } from 'rxjs';
import { combineLatest, merge as observableMerge, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pluck, takeUntil } from 'rxjs/operators';

import { NgTemplateAssertType } from '@recruitee/common';

import { TypeaheadInputDirective } from './typeahead-input.directive';
import { TypeaheadSuggestionsComponent } from './typeahead-suggestions.component';
import type { Suggestion, TypeaheadSuggestionsState } from './typeahead.service';
import {
  EMPTY_STATE,
  HIDDEN_STATE,
  LOADING_STATE,
  SUGGESTIONS_STATE,
  TypeaheadService,
} from './typeahead.service';
import { ConnectedOverlayDirective } from '../../core/overlay/overlay-directives';
import { InputComponent } from '../input/input.component';

export interface SuggestionsContext<S> {
  typeaheadstate: TypeaheadSuggestionsState<S>;
}

@Component({
  selector: 'rt-typeahead',
  exportAs: 'rtTypeahead',
  templateUrl: './typeahead.component.html',
  host: {
    '(keydown.arrowUp)': 'keyArrowUp($event)',
    '(keydown.arrowDown)': 'keyArrowDown($event)',
    '(keydown.enter)': 'keyEnter()',
  },
  providers: [TypeaheadService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ConnectedOverlayDirective,
    NgTemplateOutlet,
    TypeaheadSuggestionsComponent,
    NgTemplateAssertType,
  ],
})
export class TypeaheadComponent {
  @Input() public value: string;
  @Input() public active: boolean;
  @Input() public loading: boolean;
  @Input() public mapWidth: boolean = true;
  @Input() public suggestionEnabled: boolean = true;
  @Input() public inputDebounce: number = 300;
  @Input() public minLength: number = 3;
  @Input() public autoFocus: boolean = false;
  @Input()
  public positions: ConnectionPositionPair[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];
  @Input() public suggestionsDropdownTpl?: TemplateRef<any>;
  @Input() public suggestions: Suggestion[] = [];
  @Input() public greedy: boolean;

  public changed = output<string>();
  public selected = output<Suggestion>();

  @ViewChild(ConnectedOverlayDirective, { static: true }) public overlay: ConnectedOverlayDirective;
  @ContentChild(TypeaheadInputDirective, { static: true, read: NgControl })
  public input: NgControl;
  @ContentChild(InputComponent, { static: true }) public inputComponent: InputComponent;

  public suggestionsContext: SuggestionsContext<Suggestion>;

  private value$ = new Subject<string>();
  private active$ = new Subject<boolean>();
  private ngOnDestroy$ = new Subject<void>();
  private _width: number | string;

  constructor(
    // we use 'this' as [origin] for rtConnectedOverlay directive therefore elementRef must be defined
    public elementRef: ElementRef,
    private vcRef: ViewContainerRef,
    private service: TypeaheadService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public get width(): number | string {
    if (this.mapWidth) {
      return `${this.elementRef.nativeElement.getBoundingClientRect().width}px`;
    } else {
      return this._width;
    }
  }

  @Input()
  public set width(val: number | string) {
    this._width = val;
  }

  public get data$(): Observable<TypeaheadSuggestionsState> {
    return this.service.data$;
  }

  public ngOnInit(): void {
    this.data$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(data => {
      this.suggestionsContext = { typeaheadstate: data };
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.suggestions) {
      this.service.updateSuggestions(this.suggestions);
    }

    if (changes.greedy) {
      this.service.greedy = this.greedy;
    }

    if (changes.value) {
      this.value$.next(this.value);
    }

    if (changes.active) {
      this.active$.next(this.active);
    }

    if (changes.loading) {
      this.service.setState({
        ...this.service.state,
        loading: this.loading,
      });
    }
  }

  // eslint-disable-next-line max-lines-per-function
  public ngAfterViewInit(): void {
    let valueAccessor;
    let value;
    let active;
    let value$: Observable<string>;
    let active$: Observable<boolean>;

    if (this.input) {
      valueAccessor = this.input.valueAccessor;
      value = this.input.value;
      active = false;
      value$ = this.input.valueChanges!.pipe(
        filter(
          val => this.minLength === 0 || (this.input.valid && val && val.length >= this.minLength),
        ),
        distinctUntilChanged(),
      );
      // @ts-ignore
      active$ = observableMerge(valueAccessor.focus, valueAccessor.blur).pipe(
        map((evt: Event) => evt.type === 'focus'),
      );
    } else {
      value = this.value;
      value$ = this.value$;
      active = this.active;
      active$ = this.active$;
    }

    const state$ = this.service.setup({
      initial: {
        value,
        active,
      },
      streams: {
        value$,
        active$,
      },
    });

    value$
      .pipe(debounceTime(this.inputDebounce), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(val => {
        if (val !== this.service.state.selected?.value) {
          this.changed.emit(val);
        }
      });

    state$
      .pipe(pluck('name'), distinctUntilChanged(), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(name => {
        switch (name) {
          case HIDDEN_STATE:
            if (this.overlay.open) {
              this.overlay.open = false;
            }
            break;

          case LOADING_STATE:
          case EMPTY_STATE:
          case SUGGESTIONS_STATE:
            if (!this.overlay.open) {
              this.overlay.open = true;
            }
            break;

          default:
            break;
        }
      });

    combineLatest([
      state$.pipe(pluck('highlighted')),
      state$.pipe(pluck('suggestions')),
      state$.pipe(pluck('name')),
    ])
      .pipe(distinctUntilChanged(deepEqual), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        if (this.overlay.open) {
          this.cdRef.detectChanges();
        }
      });

    combineLatest([state$.pipe(pluck('suggestions')), state$.pipe(pluck('name'))])
      .pipe(distinctUntilChanged(deepEqual), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        if (this.overlay.open) {
          setTimeout(() => {
            this.overlay.updatePositionWithoutRedrawing();
          });
        }
      });

    if (this.autoFocus && this.inputComponent) {
      this.inputComponent.setFocus();
    }
  }

  public ngOnDestroy(): void {
    this.service.cleanup();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public select(suggestion: Suggestion): void {
    this.service.select(suggestion);
    this.updateControl(suggestion);
    this.selected.emit(suggestion);
  }

  public updateControl(suggestion: Suggestion): void {
    if (this.input) {
      this.input.control!.setValue(suggestion.value);
      this.cdRef.detectChanges();
    }
  }

  public keyArrowUp($event: KeyboardEvent): void {
    if (this.overlay.open) {
      this.service.highlightPrev();
      this.scrollToHighlighted();
      $event.stopImmediatePropagation();
    }
  }

  public keyArrowDown($event: KeyboardEvent): void {
    if (this.overlay.open) {
      this.service.highlightNext();
      this.scrollToHighlighted();
      $event.stopImmediatePropagation();
    }
  }

  public keyEnter(): void {
    if (this.overlay.open) {
      const selected = this.service.selectHighlighted();
      if (selected) {
        this.updateControl(selected);
        this.selected.emit(selected);
      }

      this.overlay.open = false;
    }
  }

  private scrollToHighlighted(): void {
    setTimeout(() => {
      const overlayEl = this.overlay.overlayRef.overlayElement;
      if (!overlayEl) return;
      const highlightedEl = overlayEl.querySelector('.highlighted');
      if (!highlightedEl) return;

      if (highlightedEl.getBoundingClientRect().bottom > overlayEl.getBoundingClientRect().bottom) {
        const scrollable = this.firstScrollableParent(highlightedEl);
        if (!scrollable) return;
        scrollable.scrollTop =
          highlightedEl.getBoundingClientRect().bottom - overlayEl.getBoundingClientRect().bottom;
      }
    });
  }

  private firstScrollableParent(node: Element): Element | null {
    if (!node) return null;

    if (node.scrollHeight > node.clientHeight) {
      return node;
    } else {
      return this.firstScrollableParent(<HTMLElement>node.parentNode);
    }
  }
}
